/* favo.ts — a geometria da colmeia.
 *
 * Funções puras, sem DOM e sem React: só trigonometria. Ficam aqui (e não
 * dentro de um componente) porque três primitivos diferentes desenham
 * hexágono — o marcador, o favo de cem células e a régua de indicador — e um
 * hexágono desenhado por três fórmulas parecidas acaba com três aparências
 * diferentes na mesma tela.
 *
 * Duas orientações, e elas não são intercambiáveis:
 *   ponta  ponta em cima, laterais retas. É a célula empilhável do favo e é a
 *          forma do logotipo da FAVO.
 *   plano  topo achatado. Serve de recipiente: o nível de líquido atravessa a
 *          largura cheia em vez de morrer no canto que estreita. */

export type OrientacaoHexagono = 'ponta' | 'plano';

/** Razão entre a largura e o raio de um hexágono de ponta para cima. */
export const LARGURA_POR_RAIO = Math.sqrt(3);

/**
 * Os seis vértices, prontos para o atributo `points` de um `<polygon>`.
 * `r` é o raio do círculo circunscrito (centro até o vértice).
 */
export function pontosHexagono(
  cx: number,
  cy: number,
  r: number,
  orientacao: OrientacaoHexagono = 'ponta',
): string {
  const giro = orientacao === 'ponta' ? -90 : 0;
  const vertices: string[] = [];
  for (let i = 0; i < 6; i++) {
    const angulo = ((60 * i + giro) * Math.PI) / 180;
    vertices.push(`${(cx + r * Math.cos(angulo)).toFixed(2)},${(cy + r * Math.sin(angulo)).toFixed(2)}`);
  }
  return vertices.join(' ');
}

export type CelulaDoFavo = { indice: number; cx: number; cy: number };

export type GradeDoFavo = {
  largura: number;
  altura: number;
  celulas: CelulaDoFavo[];
};

/**
 * Um favo retangular de `total` células em `colunas` colunas, com as linhas
 * ímpares deslocadas meia célula — que é o que faz um favo ser um favo e não
 * uma grade de losangos.
 */
export function gradeDoFavo(
  total: number,
  colunas: number,
  r: number,
  margem = 0,
): GradeDoFavo {
  const largura = LARGURA_POR_RAIO * r;
  const passoY = 1.5 * r;
  const linhas = Math.ceil(total / colunas);

  const celulas: CelulaDoFavo[] = [];
  for (let indice = 0; indice < total; indice++) {
    const linha = Math.floor(indice / colunas);
    const coluna = indice % colunas;
    celulas.push({
      indice,
      cx: margem + largura / 2 + coluna * largura + (linha % 2) * (largura / 2),
      cy: margem + r + linha * passoY,
    });
  }

  return {
    // A meia célula extra na largura é o deslocamento das linhas ímpares.
    largura: margem * 2 + largura * colunas + largura / 2,
    altura: margem * 2 + passoY * (linhas - 1) + 2 * r,
    celulas,
  };
}

/**
 * Reparte `total` células entre fatias que somam percentuais quaisquer,
 * SEM perder nem inventar célula: arredonda para baixo e distribui o resto
 * pelas maiores sobras (método do maior resto).
 *
 * Isto é arredondamento de desenho, não conta de negócio: define quantos
 * hexágonos pintar, e o valor que o leitor confere continua vindo do motor.
 * Arredondar cada fatia isoladamente daria 99 ou 101 células, e um favo com
 * uma célula sobrando é exatamente o tipo de detalhe que faz alguém duvidar
 * do número ao lado.
 */
export function repartirCelulas(percentuais: readonly number[], total = 100): number[] {
  const soma = percentuais.reduce((s, p) => s + p, 0);
  if (soma <= 0) return percentuais.map(() => 0);

  const exatos = percentuais.map((p) => (p / soma) * total);
  const base = exatos.map((v) => Math.floor(v));
  let resto = total - base.reduce((s, v) => s + v, 0);

  const ordem = exatos
    .map((v, i) => ({ i, sobra: v - Math.floor(v) }))
    .sort((a, b) => b.sobra - a.sobra);

  for (let k = 0; resto > 0 && k < ordem.length; k++, resto--) {
    const alvo = ordem[k];
    if (alvo) base[alvo.i] = (base[alvo.i] ?? 0) + 1;
  }
  return base;
}

/**
 * A cor da fatia `indice` numa escala SEQUENCIAL de três posições.
 *
 * Por que sequencial e não categórica: as fatias de custo não são
 * identidades soltas, são posições numa mesma jornada — o ticket entra e vai
 * sendo consumido em ordem. A versão categórica foi medida e reprovou em
 * daltonismo (ΔE2000 2,1 sob protanopia contra o piso de 6,4); esta, com
 * TRÊS posições, tem separação mínima de 11,4 nas quatro visões testadas.
 *
 * Com mais de três fatias de custo a escala interpola, e a separação cai
 * proporcionalmente: aí o nome e o valor na legenda passam a ser o que
 * distingue, não a cor. Agrupar custo em três é a leitura mais honesta.
 */
export function corDaFatia(indice: number, quantasFatias: number): string {
  if (quantasFatias <= 1) return 'var(--cascata-1)';
  const t = indice / (quantasFatias - 1);
  if (t <= 0) return 'var(--cascata-1)';
  if (t >= 1) return 'var(--cascata-3)';
  if (t < 0.5) {
    const p = Math.round(t * 200);
    return `color-mix(in oklab, var(--cascata-2) ${p}%, var(--cascata-1))`;
  }
  const p = Math.round((t - 0.5) * 200);
  return `color-mix(in oklab, var(--cascata-3) ${p}%, var(--cascata-2))`;
}
